import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Users, History, CalendarDays, Loader2 } from 'lucide-react';
import { bolsa as bolsaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function BolsaDias() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('empresa'); // 'empresa' | 'companeros'

    const { user } = useAuth();
    const [loading, setLoading] = useState(true);

    // State placeholders for data injection
    const [saldoEmpresa, setSaldoEmpresa] = useState({ total: 0, generados: 0, devueltos: 0 });
    const [historialEmpresa, setHistorialEmpresa] = useState([]);
    const [saldoCompaneros, setSaldoCompaneros] = useState({ teDeben: 0, debes: 0 });
    const [desgloseCompaneros, setDesgloseCompaneros] = useState([]);

    useEffect(() => {
        const fetchBolsa = async () => {
            if (!user?.conductorId) {
                setLoading(false);
                return;
            }
            try {
                const data = await bolsaApi.get({ conductor_id: user.conductorId });
                
                // Parse empresa
                const empresaData = data.filter(d => d.tipo_entidad === 'empresa');
                let totalEmpresa = 0;
                let generadosEmpresa = 0;
                let devueltosEmpresa = 0;
                
                const histEmpresaMap = empresaData.map(item => {
                    totalEmpresa += item.valor;
                    if (item.valor > 0) generadosEmpresa += item.valor;
                    else devueltosEmpresa += Math.abs(item.valor);
                    
                    return {
                        tipo: item.valor > 0 ? 'generado' : 'devuelto',
                        valor: item.valor,
                        descripcion: item.descripcion || (item.valor > 0 ? 'Trabajado en día libre' : 'Día libre devuelto'),
                        fechaText: new Date(item.fecha_operacion).toLocaleDateString('es-ES')
                    };
                });
                
                setSaldoEmpresa({ total: totalEmpresa, generados: generadosEmpresa, devueltos: devueltosEmpresa });
                setHistorialEmpresa(histEmpresaMap);
                
                // Parse companeros
                const compsData = data.filter(d => d.tipo_entidad === 'companero');
                let teDeben = 0;
                let debes = 0;
                
                const desgloseMap = {};
                compsData.forEach(item => {
                    const compKey = item.companero_nick || item.companero_nombre || 'Compañero desconocido';
                    if (!desgloseMap[compKey]) {
                        desgloseMap[compKey] = { nombre: compKey, saldo: 0, movimientos: [] };
                    }
                    
                    desgloseMap[compKey].saldo += item.valor;
                    if (item.valor > 0) teDeben += item.valor;
                    else debes += Math.abs(item.valor);
                    
                    desgloseMap[compKey].movimientos.push({
                        valor: item.valor,
                        descripcion: `${new Date(item.fecha_operacion).toLocaleDateString('es-ES')} - ${item.descripcion || 'CT'}`
                    });
                });
                
                setSaldoCompaneros({ teDeben, debes });
                setDesgloseCompaneros(Object.values(desgloseMap));
                
            } catch (error) {
                console.error("Error fetching bolsa de dias:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBolsa();
    }, [user]);

    return (
        <div className="pb-24 min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 to-amber-600 px-4 py-4 sm:py-6 rounded-b-3xl shadow-lg relative mb-6">
                <div className="flex items-center gap-4 mb-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-black text-white leading-tight">Bolsa de Días</h1>
                        <p className="text-xs sm:text-sm text-orange-100 font-medium">Historial de días a deber</p>
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab('empresa')}
                        className={`flex-1 py-2 sm:py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors text-xs sm:text-sm font-black ${activeTab === 'empresa'
                                ? 'bg-white text-orange-600 shadow-md'
                                : 'bg-white/20 text-white hover:bg-white/30'
                            }`}
                    >
                        <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        Empresa
                    </button>
                    <button
                        onClick={() => setActiveTab('companeros')}
                        className={`flex-1 py-2 sm:py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors text-xs sm:text-sm font-black ${activeTab === 'companeros'
                                ? 'bg-white text-orange-600 shadow-md'
                                : 'bg-white/20 text-white hover:bg-white/30'
                            }`}
                    >
                        <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                        Compañeros
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="px-4 sm:px-6 space-y-4">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-orange-500">
                        <Loader2 className="w-8 h-8 animate-spin mb-4" />
                        <p className="text-sm font-medium">Cargando bolsa de días...</p>
                    </div>
                ) : activeTab === 'empresa' ? (
                    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                        {/* Resumen Saldo Empresa */}
                        <div className="bg-[var(--bg-secondary)] rounded-2xl p-5 border border-[var(--glass-border)] shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
                            
                            <h2 className="text-orange-500 font-black tracking-widest text-xs uppercase mb-4 flex items-center gap-2">
                                <Building2 className="w-4 h-4" />
                                Saldo con Empresa
                            </h2>
                            
                            <div className="flex justify-between items-end">
                                <div>
                                    <div className={`text-3xl sm:text-4xl font-black ${saldoEmpresa.total >= 0 ? 'text-[var(--text-primary)]' : 'text-red-500'}`}>
                                        {saldoEmpresa.total > 0 ? '+' : ''}{saldoEmpresa.total}
                                    </div>
                                    <div className="text-xs text-[var(--text-muted)] font-medium mt-1">Días a tu favor</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-[var(--text-primary)]">Trabajados: {saldoEmpresa.generados}</div>
                                    <div className="text-sm font-bold text-[var(--text-muted)]">Devueltos: {saldoEmpresa.devueltos}</div>
                                </div>
                            </div>
                        </div>

                        {/* Historial Empresa */}
                        <h3 className="font-bold text-[var(--text-primary)] mt-6 mb-3 flex items-center gap-2">
                            <History className="w-4 h-4 text-orange-500" />
                            Historial
                        </h3>
                        
                        <div className="space-y-3">
                            {historialEmpresa.length === 0 ? (
                                <div className="text-center py-8 text-[var(--text-secondary)] text-sm font-medium border border-dashed border-[var(--glass-border)] rounded-xl">
                                    No hay registros con la empresa
                                </div>
                            ) : (
                                historialEmpresa.map((item, idx) => (
                                    <div key={idx} className={`bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4 flex items-center justify-between ${item.tipo === 'devuelto' ? 'opacity-70' : ''}`}>
                                        <div>
                                            <p className="text-sm font-bold text-[var(--text-primary)] flex items-center gap-2">
                                                <span className={`w-2 h-2 rounded-full ${item.tipo === 'devuelto' ? 'bg-red-400' : 'bg-emerald-400'}`}></span> 
                                                {item.descripcion}
                                            </p>
                                            <p className="text-xs text-[var(--text-muted)] mt-1">{item.fechaText}</p>
                                        </div>
                                        <div className={`${item.tipo === 'devuelto' ? 'text-red-400' : 'text-emerald-400'} font-black text-lg`}>
                                            {item.valor > 0 ? '+' : ''}{item.valor}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                        {/* Resumen Saldo Compañeros */}
                        <div className="bg-[var(--bg-secondary)] rounded-2xl p-5 border border-[var(--glass-border)] shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
                            
                            <h2 className="text-orange-500 font-black tracking-widest text-xs uppercase mb-4 flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Balance de Cambios (CT)
                            </h2>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-3 shadow-sm">
                                    <div className="text-xs text-[var(--text-muted)] font-medium mb-1">Te deben</div>
                                    <div className="text-2xl font-black text-emerald-500">+{saldoCompaneros.teDeben}</div>
                                </div>
                                <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-3 shadow-sm">
                                    <div className="text-xs text-[var(--text-muted)] font-medium mb-1">Debes</div>
                                    <div className="text-2xl font-black text-red-500">-{saldoCompaneros.debes}</div>
                                </div>
                            </div>
                        </div>

                        {/* Desglose por compañeros */}
                        <h3 className="font-bold text-[var(--text-primary)] mt-6 mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4 text-orange-500" />
                            Desglose por compañero
                        </h3>
                        
                        <div className="space-y-3">
                            {desgloseCompaneros.length === 0 ? (
                                <div className="text-center py-8 text-[var(--text-secondary)] text-sm font-medium border border-dashed border-[var(--glass-border)] rounded-xl">
                                    No hay intercambios con compañeros
                                </div>
                            ) : (
                                desgloseCompaneros.map((comp, idx) => (
                                    <div key={idx} className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4">
                                        <div className="flex justify-between items-center mb-2">
                                            <p className="font-bold text-[var(--text-primary)]">{comp.nombre}</p>
                                            <span className={`text-xs font-black px-2 py-1 rounded ${comp.saldo > 0 ? 'bg-emerald-500/20 text-emerald-400' : comp.saldo < 0 ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-[var(--text-muted)]'}`}>
                                                {comp.saldo > 0 ? `A tu favor: ${comp.saldo}` : comp.saldo < 0 ? `Le debes: ${Math.abs(comp.saldo)}` : 'Saldados: 0'}
                                            </span>
                                        </div>
                                        <div className="text-xs text-[var(--text-muted)] space-y-2 mt-2 pt-2 border-t border-[var(--glass-border)]">
                                            {comp.movimientos.map((mov, midx) => (
                                                <div key={midx} className="flex items-center gap-2">
                                                    <CalendarDays className={`w-3 h-3 ${mov.valor > 0 ? 'text-emerald-400/50' : 'text-red-400/50'}`} />
                                                    <span>{mov.descripcion}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
